import React, { useState, useRef } from 'react';
import { 
  Settings, 
  Cloud, 
  Download, 
  Upload, 
  RefreshCw, 
  CheckCircle2, 
  AlertCircle, 
  Save, 
  Database 
} from 'lucide-react';
import { useStore, setState } from '../../hooks/useStore.js';
import { exportBackupJson, createManualBackup } from '../../state/backups.js';
import { saveLocalData } from '../../state/storage.js';
import { syncWithSupabase, syncAfterLocalChange } from '../../services/sync.js';

export function SettingsView() {
  const settings = useStore(s => s.settings) || {};
  const isSyncing = useStore(s => s.cloudSyncInProgress);
  const lastSync = useStore(s => s.lastCloudSyncAt);

  const [threshold, setThreshold] = useState(settings.lowStockThreshold || 5);
  const [locations, setLocations] = useState(settings.storageLocations || 'ตู้ A, ตู้ B, ตู้ C, ชั้นวาง 1, ชั้นวาง 2');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);

  const handleSaveSettings = () => {
    try {
      const newSettings = {
        ...settings,
        lowStockThreshold: Number(threshold) || 5,
        storageLocations: locations.trim()
      };
      setState({ settings: newSettings });
      saveLocalData();
      syncAfterLocalChange('settings-update');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      setErrorMsg('เกิดข้อผิดพลาดในการบันทึกการตั้งค่า: ' + e.message);
    }
  };

  const handleExportBackup = () => {
    try {
      exportBackupJson();
    } catch (e) {
      setErrorMsg('เกิดข้อผิดพลาดในการดาวน์โหลดข้อมูลสำรอง: ' + e.message);
    }
  };

  const handleManualSync = async () => {
    if (isSyncing) return;
    try {
      await syncWithSupabase();
    } catch (e) {
      setErrorMsg('การซิงค์ข้อมูลไม่สำเร็จ: ' + e.message);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 touch-scroll">
      {/* Page Title */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-xs">
          <Settings className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-xl font-extrabold text-slate-900 leading-tight">ตั้งค่าระบบ (Settings)</h2>
          <p className="text-xs text-slate-700 font-medium">จัดการข้อมูลสำรอง การแจ้งเตือน และการเชื่อมต่อคลาวด์</p>
        </div>
      </div>

      {saveSuccess && (
        <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-600" />
          <span>บันทึกการตั้งค่าเรียบร้อยแล้ว</span>
        </div>
      )}

      {errorMsg && (
        <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs font-semibold flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 text-red-600" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Cloud Sync Status Card */}
      <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-slate-200/80 shadow-xs space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-cyan-50 border border-cyan-100 flex items-center justify-center text-cyan-800">
              <Cloud className="w-5 h-5 text-cyan-700" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">Supabase Cloud Sync</h3>
              <div className="text-xs text-slate-700 font-medium">
                {lastSync ? `ซิงค์ล่าสุดเมื่อ: ${new Date(lastSync).toLocaleString('th-TH')}` : 'เชื่อมต่อเซิร์ฟเวอร์คลาวด์อัตโนมัติ'}
              </div>
            </div>
          </div>

          <button
            onClick={handleManualSync}
            disabled={isSyncing}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold border border-indigo-200 transition-colors cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
            <span>{isSyncing ? 'กำลังซิงค์...' : 'บังคับซิงค์เดี๋ยวนี้'}</span>
          </button>
        </div>
      </div>

      {/* Thresholds & Locations Card */}
      <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-slate-200/80 shadow-xs space-y-5">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Database className="w-4 h-4 text-indigo-600" />
          <span>เกณฑ์แจ้งเตือนสต็อกและสถานที่จัดเก็บ</span>
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-800 mb-1">
              เกณฑ์แจ้งเตือนสต็อกเหลือน้อย (ชิ้น)
            </label>
            <input
              type="number"
              min="0"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="w-full px-3 py-2 text-sm font-bold rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
            <span className="text-[11px] text-slate-600 font-medium mt-1 block">
              ระบบจะแสดงเตือนเมื่อยอดรวมสินค้าน้อยกว่าหรือเท่ากับค่านี้
            </span>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-800 mb-1">
              รายชื่อสถานที่จัดเก็บ / ตู้พัสดุ
            </label>
            <textarea
              rows={3}
              value={locations}
              onChange={(e) => setLocations(e.target.value)}
              placeholder="แยกแต่ละสถานที่ด้วยเครื่องหมายจุลภาค (,)"
              className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-semibold"
            />
            <span className="text-[11px] text-slate-600 font-medium mt-0.5 block">
              คั่นแต่ละสถานที่ด้วยเครื่องหมายจุลภาค (,) เช่น ตู้ A, ตู้ B, ชั้นวาง 1
            </span>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={handleSaveSettings}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-xs transition-colors cursor-pointer"
          >
            <Save className="w-4 h-4" />
            <span>บันทึกการตั้งค่า</span>
          </button>
        </div>
      </div>

      {/* Backup and Export Card */}
      <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-slate-200/80 shadow-xs space-y-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Download className="w-4 h-4 text-emerald-600" />
          <span>สำรองและนำเข้าข้อมูล (Data Backup & Export)</span>
        </h3>

        <p className="text-xs text-slate-700 font-medium leading-relaxed">
          สามารถดาวน์โหลดไฟล์ JSON ข้อมูลทั้งหมดในระบบ (คลังสินค้า, ประวัติการยืม-คืน, ผลการตรวจนับ) 
          เพื่อสำรองเก็บไว้ในเครื่องคอมพิวเตอร์ของคุณได้อย่างปลอดภัย
        </p>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            onClick={handleExportBackup}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs shadow-xs transition-colors cursor-pointer"
          >
            <Download className="w-4 h-4" />
            <span>ส่งออกไฟล์สำรองข้อมูล (JSON Backup)</span>
          </button>
        </div>
      </div>
    </div>
  );
}
